import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTopicResponse, TopicSort } from '@mmf/contracts';
import { fetchCategories, fetchMainCategories, fetchTopics } from '../api/client';
import { moderationBadgeText } from './moderationBadge';
import { NewTopicForm } from './NewTopicForm';
import { PostFeedback } from './PostFeedback';
import { VoteButtons } from './VoteButtons';

interface TopicListProps {
  /** Absent for `/discussions/all` — the flat combined view, no category
   * filter, no posting (there's no main category to post into). Present for
   * `/discussions/:mainCategoryId` — adds the category filter and "Start a
   * Discussion", both scoped to that main category's own sub-categories. */
  mainCategoryId?: string;
}

/** Route element for `/discussions/:mainCategoryId` — reads the param and
 * hands it down, so `TopicList` itself stays route-agnostic and reusable
 * for `/discussions/all` (no prop) too. */
export function ScopedTopicList(): React.JSX.Element {
  const { mainCategoryId } = useParams<{ mainCategoryId: string }>();
  return <TopicList mainCategoryId={mainCategoryId} />;
}

export function TopicList({ mainCategoryId }: TopicListProps): React.JSX.Element {
  const queryClient = useQueryClient();
  // Read once on mount so the landing page's category cards can deep-link
  // straight into a filtered view.
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState<string>(() => searchParams.get('category') ?? '');
  const [mine, setMine] = useState<boolean>(() => searchParams.get('mine') === 'true');
  const [sort, setSort] = useState<TopicSort>('recent');
  const [queryInput, setQueryInput] = useState('');
  const [q, setQ] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [postFeedback, setPostFeedback] = useState<CreateTopicResponse | null>(null);

  // Reuses the picker page's cache when it's already warm — no extra fetch
  // just to show this main category's own name in the heading.
  const mainCategories = useQuery({
    queryKey: ['main-categories'],
    queryFn: fetchMainCategories,
    enabled: Boolean(mainCategoryId),
  });
  const mainCategoryName = mainCategories.data?.find((mc) => mc.id === mainCategoryId)?.name;

  const categories = useQuery({
    queryKey: ['categories', mainCategoryId],
    queryFn: () => fetchCategories(mainCategoryId as string),
    enabled: Boolean(mainCategoryId),
  });

  const topics = useQuery({
    queryKey: ['topics', mainCategoryId ?? 'all', category || 'all', sort, mine, q],
    queryFn: () =>
      fetchTopics({
        mainCategoryId,
        category: category || undefined,
        sort,
        mine: mine || undefined,
        q: q || undefined,
      }),
  });

  return (
    <section aria-labelledby="topic-list-heading">
      <div className="topic-list-header">
        <div>
          <Link to="/discussions" className="back-link">
            ← All main categories
          </Link>
          <h2 id="topic-list-heading">{mainCategoryId ? (mainCategoryName ?? 'Discussions') : 'All Discussions'}</h2>
        </div>
        {mainCategoryId && !isComposing && (
          <button
            type="button"
            className="button"
            onClick={() => setIsComposing(true)}
            data-testid="start-discussion"
          >
            Start a Discussion
          </button>
        )}
      </div>

      {mainCategoryId && isComposing && categories.data && (
        <NewTopicForm
          categories={categories.data}
          onCancel={() => setIsComposing(false)}
          onCreated={async (result) => {
            setIsComposing(false);
            setPostFeedback(result);
            await queryClient.invalidateQueries({ queryKey: ['topics'] });
          }}
        />
      )}

      {postFeedback && (
        <PostFeedback
          supportNotice={postFeedback.supportNotice}
          isHeld={postFeedback.topic.moderationState !== 'Published'}
        />
      )}

      <form
        className="topic-search"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(queryInput.trim());
        }}
      >
        <label htmlFor="topic-search-query">Find existing conversations before creating a new one</label>
        <input
          id="topic-search-query"
          value={queryInput}
          placeholder="Search title or body"
          onChange={(event) => setQueryInput(event.target.value)}
        />
        <button type="submit" className="button small" data-testid="topic-search-submit">
          Search
        </button>
        {q && (
          <button
            type="button"
            className="button secondary small"
            onClick={() => {
              setQueryInput('');
              setQ('');
            }}
            data-testid="topic-search-clear"
          >
            Clear
          </button>
        )}
      </form>

      {mainCategoryId && categories.error && (
        <p role="alert" data-testid="categories-error">
          Could not load categories. The category filter and "Start a Discussion" are unavailable
          until this loads — try refreshing.
        </p>
      )}

      <div className="topic-filters">
        {mainCategoryId && (
          <span>
            <label htmlFor="topic-category-filter">Category</label>
            <select
              id="topic-category-filter"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">All categories</option>
              {categories.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </span>
        )}

        <span>
          <label htmlFor="topic-sort">Sort</label>
          <select id="topic-sort" value={sort} onChange={(event) => setSort(event.target.value as TopicSort)}>
            <option value="recent">Recent</option>
            <option value="top">Top</option>
            <option value="trending">Trending</option>
            <option value="recentlyUpdated">Recently updated</option>
          </select>
        </span>

        <span className="checkbox">
          <input
            type="checkbox"
            id="topic-mine-filter"
            checked={mine}
            onChange={(event) => setMine(event.target.checked)}
          />
          <label htmlFor="topic-mine-filter">My discussions only</label>
        </span>
      </div>

      {topics.isLoading && <p role="status">Loading discussions…</p>}

      {topics.error && (
        <p role="alert" data-testid="topics-error">
          Could not load discussions.
        </p>
      )}

      {topics.data && topics.data.items.length === 0 && (
        <p className="hint">No discussions yet — be the first to start one.</p>
      )}

      <ul className="topic-list">
        {topics.data?.items.map((topic) => (
          <li key={topic.id} className="topic-row">
            <VoteButtons
              kind="topic"
              id={topic.id}
              score={topic.voteScore}
              myVote={topic.myVote}
              invalidateKeys={[
                ['topics'],
                ['topic', topic.id],
              ]}
            />
            <div>
              <Link to={`/topics/${topic.id}`} data-testid={`topic-link-${topic.id}`}>
                {topic.title}
              </Link>
              {moderationBadgeText(topic.moderationState) && (
                <span className="badge" data-testid={`held-badge-${topic.id}`}>
                  {' '}
                  {moderationBadgeText(topic.moderationState)}
                </span>
              )}
              <p className="hint">
                {/* Only worth spelling out the main category when this list
                    spans more than one — inside a scoped view it's implied. */}
                {!mainCategoryId && `${topic.mainCategoryName} › `}
                {topic.categoryName} · {topic.commentCount} comment{topic.commentCount === 1 ? '' : 's'} ·{' '}
                {topic.authorDisplayName}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
